import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronRight, Car, Users, Clock, Coffee, LogIn, LogOut as LogOutIcon, Trophy, TrendingUp, TrendingDown, Wrench, CalendarClock, Timer, AlertCircle, BarChart3, AlertTriangle, CarFront } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const STATUS_CONFIG = {
  open: { label: 'A faire', cls: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Signee', cls: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'En cours', cls: 'bg-amber-100 text-amber-700' },
};

const AtelierDashboardAdmin = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [monthlyRecap, setMonthlyRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [overdueAlerts, setOverdueAlerts] = useState([]);
  const [activePreps, setActivePreps] = useState([]);
  const navigate = useNavigate();
  const audioRef = useRef(null);

  const playOverdueAlarm = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;
      // Urgent alarm pattern: 3 short beeps
      for (let i = 0; i < 3; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.value = 1200;
        gain.gain.setValueAtTime(0.2, now + i * 0.25);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.25 + 0.15);
        osc.start(now + i * 0.25);
        osc.stop(now + i * 0.25 + 0.15);
      }
    } catch { /* ignore */ }
  }, []);

  // WebSocket for overdue reminders
  useEffect(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications`;
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'overdue_reminder' && data.overdue_work_orders?.length > 0) {
            setOverdueAlerts(data.overdue_work_orders);
            playOverdueAlarm();
            data.overdue_work_orders.forEach(ow => {
              const label = ow.display_label || ow.vehicle_info || ow.reference;
              const plate = ow.vehicle_plate ? ` (${ow.vehicle_plate})` : '';
              toast.warning(`Depassement: ${label}${plate}`, {
                description: `${ow.actual_hours}h / ${ow.allocated_hours}h (+${ow.overflow_hours}h)`,
                duration: 15000,
              });
            });
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, [playOverdueAlarm]);

  const loadData = useCallback(async () => {
    try {
      const [dashRes, woRes, recapRes, prepRes] = await Promise.all([
        axios.get(`${API}/atelier/dashboard`),
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/atelier/monthly-recap`),
        axios.get(`${API}/used-vehicles/preparations/active`).catch(() => ({ data: [] })),
      ]);
      setDashboard(dashRes.data);
      setWorkOrders(woRes.data);
      setMonthlyRecap(recapRes.data);
      setActivePreps(prepRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); const interval = setInterval(loadData, 30000); return () => clearInterval(interval); }, [loadData]);
  useEffect(() => { const t = setInterval(() => setTick(v => v + 1), 1000); return () => clearInterval(t); }, []);

  const activeWOs = workOrders.filter(wo => {
    if (wo.status === 'completed') return wo.atelier_visible === true;
    if (wo.atelier_ready && !wo.atelier_visible) return false;
    return true;
  });

  const today = new Date().toISOString().split('T')[0];
  const parcOccasionWOs = activeWOs.filter(wo => wo.source === 'preparation_vo');
  const livraisonWOs = activeWOs.filter(wo => wo.source === 'preparation_livraison');
  const normalWOs = activeWOs.filter(wo => wo.source !== 'preparation_vo' && wo.source !== 'preparation_livraison');
  const todayWOs = normalWOs.filter(wo => wo.appointment_date && wo.appointment_date.startsWith(today));
  const upcomingWOs = normalWOs.filter(wo => wo.appointment_date && wo.appointment_date > today).sort((a, b) => (a.appointment_date || '').localeCompare(b.appointment_date || ''));
  const otherWOs = normalWOs.filter(wo => !wo.appointment_date || (!wo.appointment_date.startsWith(today) && wo.appointment_date <= today));

  const fmtTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
  };

  const fmtHours = (mins) => {
    if (!mins || mins <= 0) return '-';
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return `${h}h${String(m).padStart(2, '0')}`;
  };

  const calcLiveHours = (clockIn) => {
    if (!clockIn) return '-';
    const start = new Date(clockIn);
    const diff = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}h${String(m).padStart(2, '0')}`;
  };

  if (loading) return <AdminLayout auth={auth} onLogout={onLogout}><p className="text-center text-slate-400 py-20">{t('common.loading')}</p></AdminLayout>;

  const attendance = dashboard?.attendance || [];
  const productivity = dashboard?.productivity || [];
  const woTimeEntries = dashboard?.wo_time_entries || [];

  const mechanicTimeMap = {};
  woTimeEntries.forEach(e => {
    const name = e.mechanic_name || 'Inconnu';
    if (!mechanicTimeMap[name]) mechanicTimeMap[name] = [];
    mechanicTimeMap[name].push(e);
  });

  const statusColors = { active: 'text-emerald-600 bg-emerald-50', on_break: 'text-amber-600 bg-amber-50', completed: 'text-slate-500 bg-slate-100', absent: 'text-red-400 bg-red-50' };
  const statusLabels = { active: 'Present', on_break: 'En pause', completed: 'Parti', absent: 'Absent' };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6" data-testid="atelier-dashboard-admin">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6 text-amber-600" /> Tableau de bord Atelier
          </h1>
          <p className="text-sm text-slate-500">
            {new Date().toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Summary cards */}
        {overdueAlerts.length > 0 && (
          <Card className="p-4 rounded-xl bg-red-50 border-2 border-red-300 animate-pulse" data-testid="overdue-alerts-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-red-800 text-sm">Depassement de temps</h3>
                <div className="mt-1 space-y-1">
                  {overdueAlerts.map(ow => (
                    <button key={ow.work_order_id} onClick={() => navigate(`/admin/work-orders/${ow.work_order_id}`)}
                      className="w-full text-left flex items-center justify-between px-2 py-1.5 rounded bg-red-100 hover:bg-red-200 transition-colors" data-testid={`overdue-${ow.work_order_id}`}>
                      <span className="text-xs font-medium text-red-900">{ow.display_label || ow.vehicle_info || ow.reference} {ow.vehicle_plate && `(${ow.vehicle_plate})`}</span>
                      <span className="text-xs font-bold text-red-700">{ow.actual_hours}h / {ow.allocated_hours}h (+{ow.overflow_hours}h)</span>
                    </button>
                  ))}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => setOverdueAlerts([])} data-testid="dismiss-overdue">
                Fermer
              </Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 rounded-xl" data-testid="stat-present">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center"><Users className="w-4 h-4 text-emerald-600" /></div>
              <div><p className="text-xl font-bold text-slate-900">{attendance.filter(a => a.status === 'active' || a.status === 'on_break').length}</p><p className="text-[10px] text-slate-500 uppercase">Presents</p></div>
            </div>
          </Card>
          <Card className="p-3 rounded-xl" data-testid="stat-today-wo">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center"><ClipboardList className="w-4 h-4 text-blue-600" /></div>
              <div><p className="text-xl font-bold text-slate-900">{todayWOs.length}</p><p className="text-[10px] text-slate-500 uppercase">Fiches du jour</p></div>
            </div>
          </Card>
          <Card className="p-3 rounded-xl" data-testid="stat-upcoming-wo">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center"><CalendarClock className="w-4 h-4 text-amber-600" /></div>
              <div><p className="text-xl font-bold text-slate-900">{upcomingWOs.length}</p><p className="text-[10px] text-slate-500 uppercase">A venir</p></div>
            </div>
          </Card>
          <Card className="p-3 rounded-xl" data-testid="stat-in-progress">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center"><Timer className="w-4 h-4 text-violet-600" /></div>
              <div><p className="text-xl font-bold text-slate-900">{otherWOs.filter(w => w.status === 'in_progress').length + todayWOs.filter(w => w.status === 'in_progress').length}</p><p className="text-[10px] text-slate-500 uppercase">{t('dashboard.in_progress')}</p></div>
            </div>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* ATTENDANCE */}
          <Card className="p-4 rounded-xl space-y-3" data-testid="attendance-section">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" /> Presence employes</h2>
            {attendance.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucun employe</p>
            ) : (
              <div className="space-y-1.5">
                {attendance.sort((a, b) => {
                  const order = { active: 0, on_break: 1, completed: 2, absent: 3 };
                  return (order[a.status] || 4) - (order[b.status] || 4);
                }).map(emp => (
                  <div key={emp.employee_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2" data-testid={`attendance-${emp.employee_id}`}>
                    <div className="flex items-center gap-2.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${emp.status === 'active' ? 'bg-emerald-600 text-white' : emp.status === 'on_break' ? 'bg-amber-500 text-white' : emp.status === 'completed' ? 'bg-slate-400 text-white' : 'bg-red-200 text-red-700'}`}>
                        {emp.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{emp.name}</p>
                        <p className="text-[10px] text-slate-400 capitalize">{emp.role || 'mecanicien'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      {emp.clock_in && <div className="flex items-center gap-1 text-slate-500"><LogIn className="w-3 h-3" /> {fmtTime(emp.clock_in)}</div>}
                      {emp.break_minutes > 0 && <div className="flex items-center gap-1 text-amber-600"><Coffee className="w-3 h-3" /> {emp.break_minutes}m</div>}
                      {emp.clock_out ? (
                        <div className="flex items-center gap-1 text-slate-500"><LogOutIcon className="w-3 h-3" /> {fmtTime(emp.clock_out)}</div>
                      ) : emp.clock_in ? (
                        <span className="font-mono font-bold text-emerald-600">{calcLiveHours(emp.clock_in)}</span>
                      ) : null}
                      {emp.total_hours != null && <span className="font-mono font-bold text-slate-700">{emp.total_hours}h</span>}
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[emp.status] || ''}`}>{statusLabels[emp.status] || emp.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* WORK TIME ON VEHICLES */}
          <Card className="p-4 rounded-xl space-y-3" data-testid="wo-time-section">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" /> Heures pointees sur vehicules</h2>
            {Object.keys(mechanicTimeMap).length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Aucun pointage aujourd'hui</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {Object.entries(mechanicTimeMap).map(([name, entries]) => {
                  const totalMins = entries.reduce((s, e) => s + (e.duration_minutes || 0), 0);
                  const hasActive = entries.some(e => !e.stopped_at);
                  return (
                    <div key={name} className="space-y-1" data-testid={`wo-time-${name.replace(/\s/g,'-').toLowerCase()}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-900">{name}</span>
                        <div className="flex items-center gap-2">
                          {hasActive && <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />}
                          <span className="text-xs font-mono font-bold text-blue-600">{fmtHours(totalMins)}</span>
                        </div>
                      </div>
                      <div className="space-y-0.5">
                        {entries.map(e => (
                          <div key={e.id} className={`flex items-center justify-between text-xs px-2 py-1 rounded ${!e.stopped_at ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50'}`}>
                            <span className="text-slate-600 truncate max-w-[200px]">{e.vehicle_info || e.work_order_id?.substring(0, 8)}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-400">{fmtTime(e.started_at)}{e.stopped_at ? ` - ${fmtTime(e.stopped_at)}` : ''}</span>
                              <span className={`font-mono font-bold ${!e.stopped_at ? 'text-emerald-600' : 'text-slate-700'}`}>{e.duration_minutes ? `${Math.round(e.duration_minutes)}m` : 'En cours'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* PRODUCTIVITY RANKING */}
        <Card className="p-4 rounded-xl space-y-3" data-testid="productivity-section">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Classement productivite</h2>
            <span className="text-xs text-slate-500">Journee : {dashboard?.daily_work_minutes ? `${Math.floor(dashboard.daily_work_minutes / 60)}h${String(dashboard.daily_work_minutes % 60).padStart(2, '0')}` : '8h15'}</span>
          </div>
          {productivity.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucune activite</p>
          ) : (
            <div className="space-y-2">
              {productivity.map((emp, idx) => {
                const pct = emp.productivity_pct;
                const barWidth = Math.min(pct, 100);
                const isFirst = idx === 0 && emp.total_minutes > 0;
                return (
                  <div key={emp.employee_id} className={`p-3 rounded-lg ${isFirst ? 'bg-amber-50 border-2 border-amber-300' : 'bg-slate-50'}`} data-testid={`rank-${emp.employee_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isFirst ? 'bg-amber-500 text-white' : 'bg-slate-900 text-white'}`}>
                          {isFirst ? <Trophy className="w-4 h-4" /> : (idx + 1)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{emp.name}</p>
                          <p className="text-[10px] text-slate-500">{emp.vehicles_count} vehicule(s) - {fmtHours(emp.total_minutes)} travaillees</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {emp.is_active && <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium"><span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" /> Actif</span>}
                        <div className={`flex items-center gap-1 text-sm font-bold ${pct >= 80 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                          {pct >= 80 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                          <span>{pct}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${barWidth}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* FICHES DU JOUR */}
        <Card className="p-4 rounded-xl space-y-3" data-testid="today-wo-section">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-600" /> Fiches du jour</h2>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{todayWOs.length}</span>
          </div>
          {todayWOs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Aucune fiche pour aujourd'hui</p>
          ) : (
            <div className="space-y-2">{todayWOs.map(wo => <WOCard key={wo.id} wo={wo} navigate={navigate} />)}</div>
          )}
        </Card>

        {otherWOs.length > 0 && (
          <Card className="p-4 rounded-xl space-y-3" data-testid="other-wo-section">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-600" /> Fiches en attente</h2>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{otherWOs.length}</span>
            </div>
            <div className="space-y-2">{otherWOs.map(wo => <WOCard key={wo.id} wo={wo} navigate={navigate} />)}</div>
          </Card>
        )}

        {upcomingWOs.length > 0 && (
          <Card className="p-4 rounded-xl space-y-3" data-testid="upcoming-wo-section">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-violet-600" /> Fiches a venir</h2>
              <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">{upcomingWOs.length}</span>
            </div>
            <div className="space-y-2">{upcomingWOs.map(wo => <WOCard key={wo.id} wo={wo} navigate={navigate} showDate />)}</div>
          </Card>
        )}

        {/* PARC OCCASION */}
        {parcOccasionWOs.length > 0 && (
          <Card className="p-4 rounded-xl space-y-3 border-l-4 border-l-cyan-500" data-testid="parc-occasion-section">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><CarFront className="w-4 h-4 text-cyan-600" /> Parc Occasion — Preparations</h2>
              <span className="text-xs bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-medium">{parcOccasionWOs.length}</span>
            </div>
            <div className="space-y-2">{parcOccasionWOs.map(wo => <WOCard key={wo.id} wo={wo} navigate={navigate} isParcOccasion />)}</div>
          </Card>
        )}

        {/* LIVRAISON */}
        {livraisonWOs.length > 0 && (
          <Card className="p-4 rounded-xl space-y-3 border-l-4 border-l-orange-500" data-testid="livraison-section">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Car className="w-4 h-4 text-orange-600" /> Livraison — Preparations</h2>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">{livraisonWOs.length}</span>
            </div>
            <div className="space-y-2">{livraisonWOs.map(wo => <WOCard key={wo.id} wo={wo} navigate={navigate} isLivraison />)}</div>
          </Card>
        )}

        {/* MONTHLY RECAP */}
        {monthlyRecap && (
          <Card className="p-4 rounded-xl space-y-4" data-testid="monthly-recap-section">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-600" /> Recapitulatif mensuel — Fiches terminees
            </h2>

            {/* Bar chart overview */}
            <div className="flex items-end gap-2 h-32" data-testid="monthly-chart">
              {[...monthlyRecap.months].reverse().map(m => {
                const maxTotal = Math.max(...monthlyRecap.months.map(x => x.total), 1);
                const barH = Math.max((m.total / maxTotal) * 100, 4);
                const isCurrent = m.month === monthlyRecap.months[0]?.month;
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-bold text-slate-700">{m.total}</span>
                    <div className={`w-full rounded-t-md transition-all ${isCurrent ? 'bg-indigo-500' : 'bg-slate-300'}`}
                      style={{ height: `${barH}%` }} />
                    <span className={`text-[10px] ${isCurrent ? 'font-bold text-indigo-600' : 'text-slate-400'}`}>{m.month}</span>
                  </div>
                );
              })}
            </div>

            {/* Detail per month with mechanics */}
            <div className="space-y-3">
              {monthlyRecap.months.map(m => (
                <div key={m.month} className={`rounded-lg border p-3 ${m.total > 0 ? 'border-slate-200 bg-slate-50' : 'border-slate-100 bg-white'}`} data-testid={`recap-${m.month.replace('/', '-')}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-slate-900">{m.month}</span>
                    <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${m.total > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>
                      {m.total} fiche{m.total !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {m.mechanics.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {m.mechanics.map((mec, i) => (
                        <span key={mec.name} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${
                          i === 0 && m.mechanics.length > 1 ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-white text-slate-600 border border-slate-200'
                        }`} data-testid={`recap-mechanic-${m.month.replace('/', '-')}-${i}`}>
                          {i === 0 && m.mechanics.length > 1 && <Trophy className="w-3 h-3 text-amber-500" />}
                          {mec.name} <span className="font-bold">{mec.count}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">Aucune fiche terminee</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

const WOCard = ({ wo, navigate, showDate, isParcOccasion, isLivraison }) => {
  const st = STATUS_CONFIG[wo.status] || STATUS_CONFIG.open;
  const autoLivraison = isLivraison || wo.source === 'preparation_livraison';
  const autoParc = isParcOccasion || wo.source === 'preparation_vo';
  const bgClass = autoLivraison ? 'bg-orange-50 hover:bg-orange-100' : autoParc ? 'bg-cyan-50 hover:bg-cyan-100' : 'bg-slate-50 hover:bg-blue-50';
  return (
    <div className={`flex items-center gap-3 p-3 ${bgClass} rounded-lg cursor-pointer transition-colors`}
      onClick={() => navigate(`/admin/work-orders/${wo.id}`)} data-testid={`wo-card-${wo.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-mono font-bold text-sm text-slate-900">{wo.number}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
          {autoParc && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-medium">Parc Occasion</span>}
          {autoLivraison && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">Livraison</span>}
          {showDate && wo.appointment_date && <span className="text-[10px] text-slate-400">{new Date(wo.appointment_date).toLocaleDateString('fr-CH')}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><Car className="w-3 h-3" /> {wo.vehicle_info || ''}</span>
          <span>{wo.client_name}</span>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
    </div>
  );
};

export default AtelierDashboardAdmin;
